/* eslint-disable no-magic-numbers */
/* eslint-disable complexity */
import { DiffDOM } from 'diff-dom';

import { STROKE_BORDER_POSITIONS, VALUE_TREATMENT } from './types';

/**
 * moves an element in an array from fromIndex to toIndex (mutable)
 */
const arrayMoveMutable = (array: unknown[], fromIndex: number, toIndex: number) => {
  const startIndex = fromIndex < 0 ? array.length + fromIndex : fromIndex;

  if (startIndex >= 0 && startIndex < array.length) {
    const endIndex = toIndex < 0 ? array.length + toIndex : toIndex;

    const [item] = array.splice(fromIndex, 1);
    array.splice(endIndex, 0, item);
  }
};

/**
 * moves an element in an array from fromIndex to toIndex (un-mutable)
 */
export const arrayMove = (array: unknown[], fromIndex: number, toIndex: number) => {
  const newArray = [...array];
  arrayMoveMutable(newArray, fromIndex, toIndex);
  return newArray;
};

/**
 * calculate data from an array, by either summing or averaging
 */
export const calculateData = (data: string[], treatment: VALUE_TREATMENT) => {
  const result = (data || []).reduce((acc, cur) => acc + (isNaN(+cur) ? 0 : +cur), 0);
  return treatment === VALUE_TREATMENT.AVERAGE && data?.length ? result / data.length : result;
};

// closest number in a sorted range
export const getClosestNumberInRange = (range: number[], n: number) =>
  range.reduce((acc, cur) => (Math.abs(cur - n) < Math.abs(cur - acc) ? cur : acc));

/**
 * checks if element is in viewport
 */
export const isElementInViewport = (el: HTMLElement) => {
  const rect = el.getBoundingClientRect();

  return rect.top < (window.innerHeight || document.documentElement.clientHeight);
};

/**
 * @param space: available space in px
 * @param rows: list of row heights in px
 * @param start: the row to start with
 * @returns an array of two values, first is the number of rows, and second is the total space they take in px
 */
export const getRowsPerAvailableSpace = (space: number, rows: number[], start: number) => {
  let s = space,
    i = 0,
    ts = 0;
  while (s - rows[start + i] > 0 && start + i < rows.length) {
    s -= rows[start + i];
    ts += rows[start + i];
    i += 1;
  }
  return [i, ts];
};

/**
 * Calculates row height based on styles
 * @param fontSize
 * @param strokePosition
 * @param stroke
 */
export const rowHeightFromStyles = (fontSize: number, strokePosition: string | undefined, stroke: number) =>
  fontSize * 1.5 +
  stroke *
    (strokePosition === STROKE_BORDER_POSITIONS.ALL_ROUND || strokePosition === STROKE_BORDER_POSITIONS.TOP_BOTTOM
      ? 2
      : strokePosition === STROKE_BORDER_POSITIONS.BOTTOM || strokePosition === STROKE_BORDER_POSITIONS.TOP
      ? 1
      : 0);

/**
 * compare if two html strings are equivalent html elements
 * @param a html string
 * @param b html string
 * @returns boolean
 */
export const areHTMLStringsEqual = (a: string, b: string) => {
  const dd = new DiffDOM();
  const diff = (dd.diff(`<div>${a}</div>`, `<div>${b}</div>`) as unknown) as Record<string, string>[];

  if (diff.length === 0) {
    return true;
  } else {
    // we should check if the changes are simply styles in different order
    for (const d of diff) {
      if (d.action === 'modifyAttribute' && d.name === 'style') {
        const oldStyles = d.oldValue.split(';').map(s => s.trim());
        const newStyles = d.newValue.split(';').map(s => s.trim());
        if (oldStyles.length !== newStyles.length) {
          return false;
        }
        if (oldStyles.some(s => !newStyles.includes(s))) {
          return false;
        }
      } else {
        return false;
      }
    }
    return true;
  }
};

/**
 * Utility function to measure the width of the label text
 * @param text
 * @param font
 * @returns
 */
export const getTextWidth = (text: string, font: string) => {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  if (context) {
    context.font = font;
    return context.measureText(text).width;
  } else {
    return 0;
  }
};

/**
 * returns true if the file extension is in the provided string
 * @param fileName
 * @param acceptedFileExtensions a list of accepted extensions separated by comma
 * @returns boolean
 */
export const checkFileExtension = (fileName: string, acceptedFileExtensions: string) => {
  const acceptedExtensions = acceptedFileExtensions.split(',');
  const fileExtension = fileName.split('.').pop()?.toLocaleLowerCase();
  return acceptedExtensions.includes(`.${fileExtension}`);
};
